import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Plus, Trash2, Play, Square, Download, Volume2 } from 'lucide-react';
import type { Keyboard } from '@/lib/microtonal/types';
import { frequencyForNote } from '@/lib/microtonal/scale';
import { 
  type PianoRollProject, type PianoRollTrack, type PianoRollNote, 
  exportMidi, downloadMidi, PPQ, GM_INSTRUMENTS, GM_INSTRUMENT_GROUPS 
} from '@/lib/microtonal/midi';
import { useAudioEngine } from '@/hooks/useAudioEngine';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface PianoRollEditorProps {
  keyboard: Keyboard;
}

const NOTE_HEIGHT = 20;
const BEAT_WIDTH = 80;
const TICKS_PER_BEAT = PPQ; // 128

const TRACK_COLORS = [
  '#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#8b5cf6', '#ef4444', '#22c55e', '#06b6d4',
  '#f97316', '#a855f7', '#10b981', '#3b82f6', '#e11d48', '#84cc16', '#0ea5e9', '#d946ef',
];

function genNoteId() {
  return `n-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function PianoRollEditor({ keyboard }: PianoRollEditorProps) {
  const { noteOn, noteOff, allNotesOff, volume, setVolume } = useAudioEngine();
  const { toast } = useToast();

  const [tracks, setTracks] = useState<PianoRollTrack[]>([
    { id: 't1', name: 'Track 1', channel: 0, program: 0, muted: false, notes: [] },
  ]);
  const [activeTrackId, setActiveTrackId] = useState('t1');
  const [bpm, setBpm] = useState(120);
  const [beatsPerBar, setBeatsPerBar] = useState(4);
  const [numBars, setNumBars] = useState(8);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartTick, setDragStartTick] = useState(0);
  const [dragKeyIndex, setDragKeyIndex] = useState(0);
  const [defaultNoteLength, setDefaultNoteLength] = useState(TICKS_PER_BEAT / 2); // 8th note

  const gridRef = useRef<HTMLDivElement>(null);
  const playbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playbackNotesRef = useRef<{ note: PianoRollNote; track: PianoRollTrack; time: number }[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPosition, setPlaybackPosition] = useState(0);

  const activeTrack = tracks.find((t) => t.id === activeTrackId) ?? tracks[0];
  
  // Reverse the key order so highest pitch is at top
  const sortedKeys = useMemo(() => {
    return [...keyboard.keys].map((k, i) => ({ key: k, index: i })).reverse();
  }, [keyboard.keys]);

  const totalBeats = numBars * beatsPerBar;
  const totalTicks = totalBeats * TICKS_PER_BEAT;
  const gridWidth = totalBeats * BEAT_WIDTH;

  const addTrack = useCallback(() => {
    if (tracks.length >= 16) {
      toast({ title: 'Maximum tracks reached', description: 'MIDI supports a maximum of 16 channels.' });
      return;
    }
    const newChannel = tracks.length;
    const newTrack: PianoRollTrack = {
      id: `t${Date.now()}`,
      name: `Track ${tracks.length + 1}`,
      channel: newChannel,
      program: 0,
      muted: false,
      notes: [],
    };
    setTracks((prev) => [...prev, newTrack]);
    setActiveTrackId(newTrack.id);
  }, [tracks.length, toast]);

  const deleteTrack = useCallback((id: string) => {
    if (tracks.length <= 1) return;
    setTracks((prev) => prev.filter((t) => t.id !== id));
    if (activeTrackId === id) {
      setActiveTrackId(tracks[0].id);
    }
  }, [tracks, activeTrackId]);

  const updateTrack = useCallback((id: string, updates: Partial<PianoRollTrack>) => {
    setTracks((prev) => prev.map((t) => (t.id === id ? { ...t, ...updates } : t)));
  }, []);

  const tickToX = useCallback((tick: number) => {
    return (tick / TICKS_PER_BEAT) * BEAT_WIDTH;
  }, []);

  const xToTick = useCallback((x: number) => {
    return Math.round((x / BEAT_WIDTH) * TICKS_PER_BEAT);
  }, []);

  const handleGridPointerDown = useCallback((e: React.PointerEvent) => {
    if (!gridRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + gridRef.current.scrollLeft;
    const y = e.clientY - rect.top + gridRef.current.scrollTop;
    
    const tick = Math.max(0, Math.min(totalTicks - 1, xToTick(x)));
    const keyRow = Math.floor(y / NOTE_HEIGHT);
    const sortedIndex = Math.max(0, Math.min(sortedKeys.length - 1, keyRow));
    const keyIndex = sortedKeys[sortedIndex].index;

    // Check if clicking on an existing note in the active track
    const existingNote = activeTrack.notes.find((n) => {
      const noteY = (sortedKeys.length - 1 - n.keyIndex) * NOTE_HEIGHT;
      return n.startTick <= tick && tick <= n.startTick + n.duration && 
             Math.abs(noteY - keyRow * NOTE_HEIGHT) < NOTE_HEIGHT;
    });

    if (existingNote) {
      // Delete the note
      setTracks((prev) => prev.map((t) => 
        t.id === activeTrack.id 
          ? { ...t, notes: t.notes.filter((n) => n.id !== existingNote.id) }
          : t
      ));
      setSelectedNoteId(null);
    } else {
      // Create a new note
      const newNote: PianoRollNote = {
        id: genNoteId(),
        keyIndex,
        startTick: tick,
        duration: defaultNoteLength,
        velocity: 80,
      };
      setTracks((prev) => prev.map((t) =>
        t.id === activeTrack.id
          ? { ...t, notes: [...t.notes, newNote] }
          : t
      ));
      setSelectedNoteId(newNote.id);
      
      // Preview the note
      const key = keyboard.keys[keyIndex];
      if (key) {
        const freq = frequencyForNote(keyboard.scale, key.degree, key.period);
        noteOn(`preview-${newNote.id}`, freq, 0.7);
        setTimeout(() => noteOff(`preview-${newNote.id}`), 200);
      }
    }
  }, [gridRef, totalTicks, xToTick, sortedKeys, activeTrack, defaultNoteLength, keyboard, noteOn, noteOff]);

  const handleNoteResize = useCallback((noteId: string, deltaTicks: number) => {
    setTracks((prev) => prev.map((t) =>
      t.id === activeTrack.id
        ? {
            ...t,
            notes: t.notes.map((n) =>
              n.id === noteId
                ? { ...n, duration: Math.max(TICKS_PER_BEAT / 8, n.duration + deltaTicks) }
                : n
            ),
          }
        : t
    ));
  }, [activeTrack]);

  const handleNoteMove = useCallback((noteId: string, deltaTick: number, deltaKey: number) => {
    setTracks((prev) => prev.map((t) =>
      t.id === activeTrack.id
        ? {
            ...t,
            notes: t.notes.map((n) =>
              n.id === noteId
                ? {
                    ...n,
                    startTick: Math.max(0, Math.min(totalTicks - n.duration, n.startTick + deltaTick)),
                    keyIndex: Math.max(0, Math.min(keyboard.keys.length - 1, n.keyIndex + deltaKey)),
                  }
                : n
            ),
          }
        : t
    ));
  }, [activeTrack, totalTicks, keyboard.keys.length]);

  const handleExport = useCallback(() => {
    const project: PianoRollProject = { keyboard, tracks, bpm, beatsPerBar };
    try {
      const data = exportMidi(project);
      downloadMidi(data, `microtonal-forge-${Date.now()}.mid`);
      toast({ title: 'MIDI exported', description: `${tracks.reduce((a, t) => a + t.notes.length, 0)} notes across ${tracks.length} track(s)` });
    } catch (err) {
      toast({ title: 'Export failed', description: String(err) });
    }
  }, [keyboard, tracks, bpm, beatsPerBar, toast]);

  // Playback
  const handlePlay = useCallback(() => {
    if (isPlaying) {
      allNotesOff();
      if (playbackTimerRef.current) clearTimeout(playbackTimerRef.current);
      setIsPlaying(false);
      setPlaybackPosition(0);
      return;
    }

    // Collect all notes sorted by start time
    const allNotes: { note: PianoRollNote; track: PianoRollTrack; time: number }[] = [];
    for (const track of tracks) {
      if (track.muted) continue;
      for (const note of track.notes) {
        const timeSeconds = (note.startTick / TICKS_PER_BEAT) * (60 / bpm);
        allNotes.push({ note, track, time: timeSeconds });
      }
    }
    allNotes.sort((a, b) => a.time - b.time);
    playbackNotesRef.current = allNotes;

    setIsPlaying(true);
    const startTime = performance.now();

    const tick = () => {
      const elapsed = (performance.now() - startTime) / 1000;
      const beatPos = elapsed * (bpm / 60);
      setPlaybackPosition(beatPos * BEAT_WIDTH);

      // Play notes that should start
      while (playbackNotesRef.current.length > 0 && playbackNotesRef.current[0].time <= elapsed) {
        const item = playbackNotesRef.current.shift()!;
        const key = keyboard.keys[item.note.keyIndex];
        if (key) {
          const freq = frequencyForNote(keyboard.scale, key.degree, key.period);
          const noteId = `playback-${item.note.id}`;
          noteOn(noteId, freq, item.note.velocity / 127);
          const durationSec = (item.note.duration / TICKS_PER_BEAT) * (60 / bpm);
          setTimeout(() => noteOff(noteId), durationSec * 1000);
        }
      }

      if (elapsed < (totalTicks / TICKS_PER_BEAT) * (60 / bpm) && playbackNotesRef.current.length > 0) {
        playbackTimerRef.current = setTimeout(tick, 50);
      } else {
        // Wait for last notes to finish
        setTimeout(() => {
          setIsPlaying(false);
          setPlaybackPosition(0);
        }, 500);
      }
    };

    tick();
  }, [isPlaying, tracks, bpm, keyboard, noteOn, noteOff, allNotesOff, totalTicks]);

  // Cleanup playback on unmount
  useEffect(() => {
    return () => {
      if (playbackTimerRef.current) clearTimeout(playbackTimerRef.current);
    };
  }, []);

  const selectedNote = activeTrack?.notes.find((n) => n.id === selectedNoteId);

  return (
    <div className="space-y-4">
      {/* Transport bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <Button
              variant={isPlaying ? 'destructive' : 'default'}
              size="sm"
              onClick={handlePlay}
            >
              {isPlaying ? <Square className="size-4 mr-1" /> : <Play className="size-4 mr-1" />}
              {isPlaying ? 'Stop' : 'Play'}
            </Button>
            
            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">BPM</Label>
              <Input type="number" min={20} max={300} value={bpm} onChange={(e) => setBpm(parseInt(e.target.value) || 120)} className="w-20" />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Bars</Label>
              <Input type="number" min={1} max={64} value={numBars} onChange={(e) => setNumBars(parseInt(e.target.value) || 8)} className="w-16" />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Beats/Bar</Label>
              <Input type="number" min={2} max={12} value={beatsPerBar} onChange={(e) => setBeatsPerBar(parseInt(e.target.value) || 4)} className="w-16" />
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap">Note Len</Label>
              <Select value={String(defaultNoteLength)} onValueChange={(v) => setDefaultNoteLength(parseInt(v))}>
                <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(TICKS_PER_BEAT * 2)}>Whole</SelectItem>
                  <SelectItem value={String(TICKS_PER_BEAT)}>Quarter</SelectItem>
                  <SelectItem value={String(TICKS_PER_BEAT / 2)}>8th</SelectItem>
                  <SelectItem value={String(TICKS_PER_BEAT / 4)}>16th</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <Volume2 className="size-4 text-muted-foreground" />
              <Slider value={[volume]} onValueChange={(v) => setVolume(v[0])} max={1} step={0.01} className="w-24" />
              <span className="text-xs text-muted-foreground w-8">{Math.round(volume * 100)}%</span>
            </div>

            <Button onClick={handleExport} size="sm" variant="outline">
              <Download className="size-4 mr-1" /> Export MIDI
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-4">
        {/* Track sidebar */}
        <div className="w-64 shrink-0 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">Tracks ({tracks.length}/16)</h3>
            <Button variant="ghost" size="icon" onClick={addTrack} className="size-7">
              <Plus className="size-4" />
            </Button>
          </div>
          
          {tracks.map((track, i) => (
            <div
              key={track.id}
              className={cn(
                'rounded-lg border p-2 space-y-2 cursor-pointer transition-colors',
                activeTrackId === track.id ? 'border-primary bg-accent' : 'border-border hover:border-muted-foreground/50'
              )}
              onClick={() => setActiveTrackId(track.id)}
            >
              <div className="flex items-center gap-2">
                <div className="size-3 rounded-full shrink-0" style={{ backgroundColor: TRACK_COLORS[track.channel % 16] }} />
                <Input
                  value={track.name}
                  onChange={(e) => { e.stopPropagation(); updateTrack(track.id, { name: e.target.value }); }}
                  onClick={(e) => e.stopPropagation()}
                  className="h-7 text-sm"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0"
                  onClick={(e) => { e.stopPropagation(); updateTrack(track.id, { muted: !track.muted }); }}
                >
                  {track.muted ? '🔇' : '🔊'}
                </Button>
                {tracks.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6 shrink-0"
                    onClick={(e) => { e.stopPropagation(); deleteTrack(track.id); }}
                  >
                    <Trash2 className="size-3" />
                  </Button>
                )}
              </div>
              
              {activeTrackId === track.id && (
                <div className="space-y-1.5 pl-1">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs w-14">Channel</Label>
                    <Select value={String(track.channel)} onValueChange={(v) => updateTrack(track.id, { channel: parseInt(v) })}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 16 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>{i + 1}{i === 9 ? ' (Drums)' : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs w-14">Instr.</Label>
                    <Select value={String(track.program)} onValueChange={(v) => updateTrack(track.id, { program: parseInt(v) })}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {GM_INSTRUMENT_GROUPS.map((group) => (
                          <SelectGroup key={group.name}>
                            <SelectLabel>{group.name}</SelectLabel>
                            {GM_INSTRUMENTS.slice(group.start, group.end + 1).map((name, idx) => (
                              <SelectItem key={group.start + idx} value={String(group.start + idx)}>
                                {group.start + idx}: {name}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
              
              <div className="text-xs text-muted-foreground">
                {track.notes.length} notes
              </div>
            </div>
          ))}
        </div>

        {/* Piano roll grid */}
        <div className="flex-1 min-w-0">
          <Card>
            <CardContent className="p-0 overflow-hidden">
              <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
                {/* Time ruler */}
                <div className="sticky top-0 z-20 bg-background border-b" style={{ width: gridWidth, minWidth: '100%' }}>
                  {Array.from({ length: numBars }, (_, bar) => (
                    <div
                      key={bar}
                      className="inline-flex items-center justify-start text-xs text-muted-foreground border-r"
                      style={{ width: beatsPerBar * BEAT_WIDTH, paddingLeft: '4px' }}
                    >
                      {bar + 1}
                    </div>
                  ))}
                </div>

                {/* Grid body */}
                <div className="relative" style={{ width: gridWidth, minWidth: '100%', height: sortedKeys.length * NOTE_HEIGHT }} ref={gridRef} onPointerDown={handleGridPointerDown}>
                  {/* Beat grid lines */}
                  <div className="absolute inset-0 pointer-events-none">
                    {Array.from({ length: totalBeats }, (_, beat) => (
                      <div
                        key={beat}
                        className={cn(
                          'absolute top-0 bottom-0 border-l',
                          beat % beatsPerBar === 0 ? 'border-border' : 'border-muted/30'
                        )}
                        style={{ left: beat * BEAT_WIDTH, width: BEAT_WIDTH }}
                      />
                    ))}
                  </div>

                  {/* Rows */}
                  {sortedKeys.map(({ key, index }, displayIndex) => {
                    const freq = frequencyForNote(keyboard.scale, key.degree, key.period);
                    const isRoot = key.degree === 0;
                    
                    return (
                      <div
                        key={key.id}
                        className={cn(
                          'relative border-b',
                          isRoot ? 'bg-amber-500/5' : displayIndex % 2 === 0 ? 'bg-muted/20' : ''
                        )}
                        style={{ height: NOTE_HEIGHT }}
                      >
                        {/* Key label */}
                        <div className="sticky left-0 z-10 bg-background/80 backdrop-blur-sm px-1 text-[9px] font-mono text-muted-foreground truncate pointer-events-none" style={{ width: 56 }}>
                          d{key.degree} p{key.period} {freq.toFixed(0)}Hz
                        </div>

                        {/* Notes in this row (from all tracks) */}
                        {tracks.map((track) => 
                          track.notes
                            .filter((n) => n.keyIndex === index)
                            .map((note) => {
                              const x = tickToX(note.startTick);
                              const width = (note.duration / TICKS_PER_BEAT) * BEAT_WIDTH;
                              const isSelected = selectedNoteId === note.id && activeTrackId === track.id;
                              return (
                                <div
                                  key={note.id}
                                  className={cn(
                                    'absolute rounded-sm border cursor-pointer transition-all z-20',
                                    isSelected && 'ring-2 ring-primary',
                                    track.muted && 'opacity-40'
                                  )}
                                  style={{
                                    left: x,
                                    top: 1,
                                    width: Math.max(4, width),
                                    height: NOTE_HEIGHT - 2,
                                    backgroundColor: TRACK_COLORS[track.channel % 16],
                                    opacity: track.muted ? 0.3 : 0.8,
                                  }}
                                  onPointerDown={(e) => {
                                    e.stopPropagation();
                                    if (activeTrackId !== track.id) {
                                      setActiveTrackId(track.id);
                                    }
                                    setSelectedNoteId(note.id);
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Delete on click if already selected
                                    if (selectedNoteId === note.id) {
                                      setTracks((prev) => prev.map((t) =>
                                        t.id === track.id
                                          ? { ...t, notes: t.notes.filter((n) => n.id !== note.id) }
                                          : t
                                      ));
                                      setSelectedNoteId(null);
                                    } else {
                                      setSelectedNoteId(note.id);
                                    }
                                  }}
                                />
                              );
                            })
                        )}
                      </div>
                    );
                  })}

                  {/* Playback position indicator */}
                  {isPlaying && (
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-30 pointer-events-none"
                      style={{ left: playbackPosition }}
                    />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Selected note inspector */}
          {selectedNote && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Selected Note</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Start (ticks)</Label>
                    <Input type="number" value={selectedNote.startTick} onChange={(e) => {
                      const tick = Math.max(0, Math.min(totalTicks - 1, parseInt(e.target.value) || 0));
                      setTracks((prev) => prev.map((t) =>
                        t.id === activeTrack.id
                          ? { ...t, notes: t.notes.map((n) => n.id === selectedNote.id ? { ...n, startTick: tick } : n) }
                          : t
                      ));
                    }} className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs">Duration</Label>
                    <Input type="number" value={selectedNote.duration} onChange={(e) => {
                      const dur = Math.max(1, parseInt(e.target.value) || 1);
                      setTracks((prev) => prev.map((t) =>
                        t.id === activeTrack.id
                          ? { ...t, notes: t.notes.map((n) => n.id === selectedNote.id ? { ...n, duration: dur } : n) }
                          : t
                      ));
                    }} className="h-8" />
                  </div>
                  <div>
                    <Label className="text-xs">Velocity (1-127)</Label>
                    <Input type="number" min={1} max={127} value={selectedNote.velocity} onChange={(e) => {
                      const vel = Math.max(1, Math.min(127, parseInt(e.target.value) || 80));
                      setTracks((prev) => prev.map((t) =>
                        t.id === activeTrack.id
                          ? { ...t, notes: t.notes.map((n) => n.id === selectedNote.id ? { ...n, velocity: vel } : n) }
                          : t
                      ));
                    }} className="h-8" />
                  </div>
                </div>
                <Button variant="destructive" size="sm" onClick={() => {
                  setTracks((prev) => prev.map((t) =>
                    t.id === activeTrack.id
                      ? { ...t, notes: t.notes.filter((n) => n.id !== selectedNote.id) }
                      : t
                  ));
                  setSelectedNoteId(null);
                }}>
                  <Trash2 className="size-4 mr-1" /> Delete Note
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
